import { Link, useLocation } from "react-router";
import { useEffect, useState } from "react";
import type { ReactPermissionRoute } from "@/stores/react/authStore";

export interface MenuProps {
    menus: ReactPermissionRoute[];
    hidden: boolean;
}

const getTitle = (route: ReactPermissionRoute) => {
    const title = route.meta?.title;
    return typeof title === "string" ? title : route.name || route.path || "";
};

const isActivePath = (pathname: string, routePath: string) =>
    pathname === routePath || (routePath !== "/" && pathname.startsWith(`${routePath}/`));

/** 分组路径可能是菜单专用路径，因此还要递归检查实际子路由是否处于活动态。 */
const containsActiveRoute = (route: ReactPermissionRoute, pathname: string): boolean =>
    isActivePath(pathname, route.path || "") ||
    (route.children || []).some(child => containsActiveRoute(child, pathname));

const getFirstSegment = (pathname: string) => `/${pathname.split("/").filter(Boolean)[0] || ""}`;

/** 对齐旧 Vue `filterHiddenRoutes`：递归隐藏路由，并移除因此失去全部子项的分组。 */
const filterVisibleMenuRoutes = (routes: ReactPermissionRoute[]): ReactPermissionRoute[] =>
    routes.reduce<ReactPermissionRoute[]>((result, route) => {
        if (route.meta?.hidden === true) {
            return result;
        }

        const children = route.children ? filterVisibleMenuRoutes(route.children) : undefined;
        if (route.children && children?.length === 0) {
            return result;
        }

        result.push(children ? { ...route, children } : route);
        return result;
    }, []);

/** 侧边导航，承接旧版菜单的活动态、折叠态和显隐态。 */
export function Menu({ menus, hidden }: MenuProps) {
    const location = useLocation();
    const activeTopPath = menus.find(menu => isActivePath(location.pathname, menu.path || ""))?.path;
    const activeTop = menus.find(menu => menu.path === activeTopPath);
    const sidebarRoutes = filterVisibleMenuRoutes(activeTop?.children || []);
    const className = ["admin-layout__aside", hidden ? "is-hidden" : ""].filter(Boolean).join(" ");

    return (
        <aside className={className} aria-label="主菜单">
            {sidebarRoutes.length
                ? sidebarRoutes.map(route => (
                      <MenuRoute key={route.path || route.name} route={route} pathname={location.pathname} />
                  ))
                : menus
                      .filter(menu => menu.path === getFirstSegment(location.pathname))
                      .map(menu => (
                          <Link key={menu.path} to={menu.path || ""} className="admin-menu-item is-active">
                              {getTitle(menu)}
                          </Link>
                      ))}
        </aside>
    );
}

function MenuRoute({ route, pathname, depth = 0 }: { route: ReactPermissionRoute; pathname: string; depth?: number }) {
    const children = route.children || [];
    const title = getTitle(route);
    const groupIsActive = containsActiveRoute(route, pathname);
    const [expanded, setExpanded] = useState(groupIsActive);

    // 路由进入或离开分组时同步展开状态；用户仍可在当前页面手动切换非活动分组。
    useEffect(() => {
        setExpanded(groupIsActive);
    }, [groupIsActive]);

    if (children.length > 0) {
        return (
            <div className={`admin-menu-group admin-menu-group--depth-${depth}`}>
                <button
                    type="button"
                    aria-expanded={expanded}
                    className={groupIsActive ? "admin-menu-group__title is-active" : "admin-menu-group__title"}
                    onClick={() => setExpanded(current => !current)}
                >
                    <span className="admin-menu-group__label">{title}</span>
                    <span aria-hidden="true" className={`admin-menu-group__arrow${expanded ? " is-expanded" : ""}`}>
                        ›
                    </span>
                </button>
                {expanded ? (
                    <div className="admin-menu-group__children">
                        {children.map(child => (
                            <MenuRoute
                                key={child.path || child.name}
                                route={child}
                                pathname={pathname}
                                depth={depth + 1}
                            />
                        ))}
                    </div>
                ) : null}
            </div>
        );
    }

    return (
        <Link
            to={route.path || ""}
            className={isActivePath(pathname, route.path || "") ? "admin-menu-item is-active" : "admin-menu-item"}
        >
            {title}
        </Link>
    );
}

export default Menu;
